import React, {useCallback, useEffect} from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { getProject, getProjectTabs, updateProject } from '../../../../features/projects';
import { clear as clearProject } from '../../../../features/projects/single';
import { filterProjects } from '../../../../features/projects/filter';
import { getAllEmployees } from '../../../../features/employees/short';
import { getAllClients } from '../../../../features/clients/filter';
import { getRoles } from '../../../../features/roles/filter';
import { PIdNameModel, Project } from '../../../../features/projects/types';
import { REmployees } from '../../../../features/roles/types';
import BaseForm from '../../../../components/shared/form-controls/base-form';
import { FormInput } from '../../../../components/shared/form-controls/form-input';
import { FormCheckbox } from '../../../../components/shared/form-controls/form-checkbox';
import { DropdownField } from '../../../../components/shared/nextgen/dropdown-field';
import FormAutocomplete from '../../../../components/shared/form-controls/form-autocomplete';
import { Lx } from '../../../../i18n/consts';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import {
    canManageProjectClient,
    canManageProjectEmployees,
    canManageProjectManager,
    canManageProjectRoleForNotification,
    canManageProjectType,
    canManageProjects,
} from '../../../../features/profile/entityPermissions';
import { PROJECT_NAME_MAX_LENGTH } from '../../constants';

interface EditForm {
    name: string;
    isActive: boolean;
    isBillable: boolean;
    client?: PIdNameModel;
    projectType: PIdNameModel;
    projectManager?: PIdNameModel;
    roleForNotification?: PIdNameModel;
    employees?: REmployees[];
    note?: string;
    isDefault?: boolean;
}

interface EditProjectLocationState {
    project: Project;
}

export default function EditProjectForm() {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as EditProjectLocationState | undefined;
    const projectId = state?.project?.id;

    const params = useParams<{ typeId?: string }>();
    const [isLoadingProject, setIsLoadingProject] = React.useState(true);
    const profile = useAppSelector(store => store.profile);
    const project = useAppSelector(store => store.project);
    const types = useAppSelector(store => store.projects.types);
    const employees = useAppSelector(store => store.rEmployees);
    const clients = useAppSelector(store => store.pClients);
    const roles = useAppSelector(store => store.eRoles);
    const [isLoadingEmployees, setIsLoadingEmployees] = React.useState(false);
    const [isLoadingClients, setIsLoadingClients] = React.useState(false);
    const [isLoadingRoles, setIsLoadingRoles] = React.useState(false);


    const resolveProjectsPath = React.useCallback((fallbackProject?: Project) => {
        const routeTypeId = params.typeId ? Number(params.typeId) : undefined;
        const typeId = fallbackProject?.projectType?.id
            ?? project.projectType?.id
            ?? routeTypeId;
        if (typeId != null && !Number.isNaN(typeId)) {
            return NAVIGATION_ROUTES.PROJECTS_BY_TYPE.replace(':typeId', String(typeId));
        }
        return NAVIGATION_ROUTES.PROJECTS;
    }, [params.typeId, project.projectType?.id]);
    const canEditProjects = canManageProjects(profile);
    const isProjectReady = !isLoadingProject && project.id === projectId;
    const canEditClient = canManageProjectClient(profile);
    const canEditProjectManager = canManageProjectManager(profile);
    const canEditEmployees = canManageProjectEmployees(profile);
    const canEditRoleForNotification = canManageProjectRoleForNotification(profile);
    const canEditProjectType = canManageProjectType(profile);

    useEffect(() => {
        if (!projectId || !canEditProjects) {
            navigate(resolveProjectsPath());
            return;
        }

        if (types.length === 0) {
            dispatch(getProjectTabs());
        }
    }, [canEditProjects, dispatch, navigate, resolveProjectsPath, projectId, types.length]);

    useEffect(() => {
        if (!projectId) {
            return;
        }

        setIsLoadingProject(true);
        dispatch(getProject(projectId))
            .unwrap()
            .finally(() => setIsLoadingProject(false));

        return () => {
            dispatch(clearProject());
        };
    }, [dispatch, projectId]);
    const ensureEmployeesLoaded = useCallback(() => {
        if (!(canEditProjectManager || canEditEmployees) || employees.length > 0 || isLoadingEmployees) {
            return;
        }

        setIsLoadingEmployees(true);
        dispatch(getAllEmployees())
            .finally(() => setIsLoadingEmployees(false));
    }, [canEditEmployees, canEditProjectManager, dispatch, employees.length, isLoadingEmployees]);

    const ensureClientsLoaded = useCallback(() => {
        if (!canEditClient || clients.length > 0 || isLoadingClients) {
            return;
        }

        setIsLoadingClients(true);
        dispatch(getAllClients())
            .finally(() => setIsLoadingClients(false));
    }, [canEditClient, clients.length, dispatch, isLoadingClients]);

    const ensureRolesLoaded = useCallback(() => {
        if (!canEditRoleForNotification || roles.length > 0 || isLoadingRoles) {
            return;
        }

        setIsLoadingRoles(true);
        dispatch(getRoles())
            .finally(() => setIsLoadingRoles(false));
    }, [canEditRoleForNotification, dispatch, isLoadingRoles, roles.length]);

    const clientOptions = clients.length ? clients : (project?.client ? [project.client] : []);
    const employeeOptions = employees.length ? employees : (project?.employees ?? []);
    const roleOptions = roles.length
        ? roles
        : (project?.roleForNotification ? [project.roleForNotification] : []);

    if (!projectId || !canEditProjects || !isProjectReady) {
        return null;
    }

    const initialValues: EditForm = {
        name: project.name,
        isActive: project.isActive,
        isBillable: project.isBillable,
        employees: project.employees,
        client: project.client,
        note: project.note,
        projectManager: project.projectManager,
        projectType: project.projectType,
        roleForNotification: project.roleForNotification,
    };

    const validateName = async (name: string): Promise<boolean> => {
        const normalized = name.trim().toLowerCase();
        if (normalized === '') {
            return Promise.resolve(false);
        }

        return dispatch(filterProjects(normalized)).unwrap().then(values => {
            return values.length < 1
                || values.some(value => value.name.toLowerCase() !== normalized)
                || normalized === project.name.toLowerCase();
        });
    };

    const save = async (values: EditForm) => {
        const payload: Project = {
            id: project.id,
            canEditEmployees: project.canEditEmployees,
            name: values.name,
            note: values.note ?? '',
            isActive: values.isActive,
            isBillable: values.isBillable,
            client: values.client,
            clientProvided: true,
            projectType: values.projectType,
            projectManager: values.projectManager,
            roleForNotification: values.roleForNotification,
            roleForNotificationProvided: true,
            employees: values.employees ?? [],
            canEditClient: project.canEditClient,
            canEditProjectManager: project.canEditProjectManager,
            canEditRoleForNotification: project.canEditRoleForNotification,
        };

        await dispatch(updateProject(payload)).unwrap();
        navigate(resolveProjectsPath(payload));
    };

    const validationScheme: Yup.Schema<EditForm> = Yup.object().shape({
        name: Yup.string()
            .max(PROJECT_NAME_MAX_LENGTH, `Must be ${PROJECT_NAME_MAX_LENGTH} characters or less`)
            .required(intl.formatMessage({ id: Lx.Form.REQUIRED }))
            .test(
                'project-name-backend-validation',
                intl.formatMessage({ id: Lx.Project.NAME_TAKEN }),
                async (name: string) => validateName(name),
            ),
        isActive: Yup.boolean().required(),
        isBillable: Yup.boolean().required(),
        projectType: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required(),
            isDefault: Yup.boolean().required(),
        }).required(),
    });

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationScheme}
            validateOnChange={false}
            validateOnBlur
            onSubmit={save}
        >
            <Form>
                <BaseForm
                    contentGap={24}
                    formType='edit'
                    formHeader={Lx.Project.EDIT_PROJECT}
                    savingTitle={intl.formatMessage({ id: Lx.General.PROJECT })}
                    elementName={project.name}
                    onClose={() => navigate(resolveProjectsPath(project))}
                    onAfterSave={() => navigate(resolveProjectsPath(project))}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '500px' }}>
                        <FormInput
                            name='name'
                            label={intl.formatMessage({ id: Lx.General.NAME })}
                            placeholder={intl.formatMessage({ id: Lx.General.NAME_PLACEHOLDER })}
                            limited
                            limit={PROJECT_NAME_MAX_LENGTH}
                            required
                        />
                        {canEditClient ? (
                            <DropdownField
                                label={intl.formatMessage({ id: Lx.General.CLIENT })}
                                name='client'
                                required={false}
                                items={clientOptions}
                                onFocus={ensureClientsLoaded}
                            />
                        ) : null}
                        {canEditProjectType ? (
                            <DropdownField
                                label={intl.formatMessage({ id: Lx.General.TYPE })}
                                name='projectType'
                                required
                                items={types.length ? types : [project.projectType]}
                            />
                        ) : null}
                        {canEditProjectManager ? (
                            <DropdownField
                                label={intl.formatMessage({ id: Lx.General.PROJECT_MANAGER })}
                                name='projectManager'
                                required={false}
                                items={employeeOptions}
                                onFocus={ensureEmployeesLoaded}
                            />
                        ) : null}
                        {canEditEmployees ? (
                            <FormAutocomplete
                                placeHolder={intl.formatMessage({ id: Lx.General.START_TYPING })}
                                label={intl.formatMessage({ id: Lx.General.EMPLOYEE })}
                                name='employees'
                                items={employeeOptions}
                                selectedItems={project.employees}
                                onFocus={ensureEmployeesLoaded}
                            />
                        ) : null}
                        {canEditRoleForNotification ? (
                            <DropdownField
                                label={intl.formatMessage({ id: Lx.General.ROLE_FOR_NOTIFICATION })}
                                name='roleForNotification'
                                required={false}
                                items={roleOptions}
                                onFocus={ensureRolesLoaded}
                            />
                        ) : null}
                        <FormInput
                            name='note'
                            label={intl.formatMessage({ id: Lx.General.NOTE })}
                            placeholder={intl.formatMessage({ id: Lx.General.WRITE_SOMETHING })}
                            required={false}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <FormCheckbox name='isActive' label={intl.formatMessage({ id: Lx.General.ACTIVE })} />
                            <FormCheckbox name='isBillable' label='Billable' />
                            <FormCheckbox name='isDefault' label={intl.formatMessage({ id: Lx.General.DEFAULT })} />
                        </div>
                    </div>
                </BaseForm>
            </Form>
        </Formik>
    );
}
